import { useMutation } from "@tanstack/react-query";
import { ShieldCheck, RefreshCw, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig, api } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import { QuestionListEditor } from "@/components/panel/QuestionListEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface Props {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["whitelist"]) => void;
}

export function WhitelistTab({
  guildId,
  config,
  channels,
  roles,
  onChange,
}: Props) {
  const cfg = config.whitelist;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });
  const questions = cfg.questions ?? [];
  const languageVariants = cfg.language_variants ?? [];
  const isMultiLanguage = cfg.language_mode === "multi";

  const repostMut = useMutation({
    mutationFn: () => api.repostPanel(guildId, { section: "whitelist" }),
    onSuccess: () =>
      toast.success("Whitelist panel message refreshed (same message, not a new post)."),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Section
      icon={ShieldCheck}
      title="Applications / Whitelist"
      description="Button + application modal + admin review."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => repostMut.mutate()}
          disabled={repostMut.isPending}
        >
          <RefreshCw
            className={`h-4 w-4 ${repostMut.isPending ? "animate-spin" : ""}`}
          />
          Repost panel
        </Button>
      }
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Configure channels and questions, then <strong>Save</strong>.</li>
          <li>
            Click <strong>Repost panel</strong> to refresh the same panel message in Discord (no duplicate posts); members
            use the apply button and modal flow.
          </li>
          <li>Staff review incoming applications in the result / notification channels you set.</li>
        </ol>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Panel channel" hint="Where the apply button is posted">
          <ChannelPicker
            channels={channels}
            value={cfg.channel_id}
            onChange={(v) => update("channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
        <Field label="Result channel" hint="Where applications are sent">
          <ChannelPicker
            channels={channels}
            value={cfg.result_channel_id}
            onChange={(v) =>
              update("result_channel_id", v === "__none__" ? "" : v)
            }
          />
        </Field>
        <Field
          label="Notification channel"
          hint="Optional. If empty, admin notifications use the result channel."
        >
          <ChannelPicker
            channels={channels}
            value={cfg.notification_channel_id ?? ""}
            onChange={(v) =>
              update("notification_channel_id", v === "__none__" ? "" : v)
            }
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Panel title" hint="Leave blank to use the default title.">
          <Input
            value={cfg.panel_title ?? ""}
            onChange={(e) => update("panel_title", e.target.value)}
            placeholder="📋 Whitelist Application"
          />
        </Field>
        <Field label="Panel description" hint="Write in as many languages as you want — this is just the intro text above the button(s).">
          <Textarea
            rows={2}
            value={cfg.panel_description ?? ""}
            onChange={(e) => update("panel_description", e.target.value)}
            placeholder="Click the button below to apply for whitelist on this server."
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          label="Panel interaction"
          hint="Buttons = component on the panel. Reactions = user reacts; bot posts a short message with the start button. Sections = Components v2 (TextDisplay + Section) with the same start action."
        >
          <Select
            value={cfg.panel_mode ?? "buttons"}
            onValueChange={(v) =>
              update(
                "panel_mode",
                v === "reactions"
                  ? "reactions"
                  : v === "sections"
                    ? "sections"
                    : "buttons",
              )
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="buttons">Buttons (components)</SelectItem>
              <SelectItem value="reactions">Reactions</SelectItem>
              <SelectItem value="sections">Sections (components v2)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {(cfg.panel_mode ?? "buttons") === "reactions" && (
          <Field label="Reaction emoji" hint="Unicode or custom <:name:id>">
            <Input
              value={cfg.panel_reaction_emoji ?? "📋"}
              onChange={(e) => update("panel_reaction_emoji", e.target.value)}
              placeholder="📋"
            />
          </Field>
        )}
      </div>

      <Field label="Admin role (approves applications)">
        <MultiSelect
          options={roles.map((r) => ({ id: r.id, name: r.name, color: r.color }))}
          value={cfg.admin_role_ids ?? []}
          onChange={(v) => update("admin_role_ids", v)}
          prefix="@"
        />
      </Field>

      <div className="space-y-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">Custom application questions</div>
            <div className="text-xs text-muted-foreground">
              If off, the bot uses the old built-in FiveM WL form.
            </div>
          </div>
          <Switch
            checked={Boolean(cfg.custom_questions_enabled)}
            onCheckedChange={(v) => update("custom_questions_enabled", v)}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Cooldown hours after submit/reject">
            <Input
              type="number"
              min={0}
              max={168}
              value={cfg.cooldown_hours ?? 24}
              onChange={(e) => update("cooldown_hours", Math.max(0, Number(e.target.value) || 0))}
            />
          </Field>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <div className="text-sm font-medium">DM accept / reject result</div>
              <div className="text-xs text-muted-foreground">Bot tries to DM the applicant.</div>
            </div>
            <Switch
              checked={cfg.dm_result ?? true}
              onCheckedChange={(v) => update("dm_result", v)}
            />
          </div>
        </div>

        <Field label="Submit confirmation">
          <Input
            value={cfg.submit_message ?? ""}
            onChange={(e) => update("submit_message", e.target.value)}
            placeholder="Application submitted..."
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Accept DM/message">
            <Textarea
              rows={2}
              value={cfg.accept_message ?? ""}
              onChange={(e) => update("accept_message", e.target.value)}
            />
          </Field>
          <Field label="Reject DM/message">
            <Textarea
              rows={2}
              value={cfg.reject_message ?? ""}
              onChange={(e) => update("reject_message", e.target.value)}
            />
          </Field>
        </div>

        <div>
          <h3 className="font-semibold">Questions</h3>
          <p className="text-xs text-muted-foreground">
            Discord modals show max 5 inputs per step; the bot splits longer forms automatically.
          </p>
        </div>

        <div className="flex gap-2 p-1 bg-muted/40 rounded-lg border border-border max-w-md">
          <button
            type="button"
            className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
              !isMultiLanguage
                ? "bg-background text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => update("language_mode", "single")}
          >
            Single language
          </button>
          <button
            type="button"
            className={`flex-1 text-xs py-1.5 rounded font-bold transition-all ${
              isMultiLanguage
                ? "bg-background text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => {
              if (isMultiLanguage) return;
              onChange({
                ...cfg,
                language_mode: "multi",
                // Only seed starter entries the first time — if you'd already built up a
                // list and just switched back to "Single language" and now back to
                // "Multiple languages", your existing entries are kept, not overwritten.
                language_variants: languageVariants.length > 0
                  ? languageVariants
                  : [
                      { id: `lv_${Date.now()}_1`, button_label: "Start Whitelist EN", questions },
                      { id: `lv_${Date.now()}_2`, button_label: "Start Whitelist SLO", questions: [] },
                    ],
              });
            }}
          >
            Multiple languages
          </button>
        </div>

        {!isMultiLanguage ? (
          <QuestionListEditor questions={questions} onChange={(next) => update("questions", next)} />
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Each language gets its own button on the panel (e.g. "Start Whitelist SLO" / "Start Whitelist EN") and its
              own question set — members pick a button, then fill in that language's form. Add as many as you need.
            </p>
            {languageVariants.map((variant, vIdx) => (
              <div key={variant.id || vIdx} className="rounded-lg border border-border/70 p-3 space-y-3 bg-muted/10">
                <div className="flex gap-2 items-center">
                  <Input
                    className="text-base sm:text-sm font-medium"
                    value={variant.button_label}
                    placeholder="Button label, e.g. Start Whitelist SLO"
                    onChange={(e) => {
                      const next = [...languageVariants];
                      next[vIdx] = { ...variant, button_label: e.target.value };
                      update("language_variants", next);
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="shrink-0"
                    onClick={() => update("language_variants", languageVariants.filter((_, i) => i !== vIdx))}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <QuestionListEditor
                  questions={variant.questions || []}
                  onChange={(next) => {
                    const nextVariants = [...languageVariants];
                    nextVariants[vIdx] = { ...variant, questions: next };
                    update("language_variants", nextVariants);
                  }}
                />
              </div>
            ))}
            {languageVariants.length === 0 && (
              <p className="text-xs text-amber-500">
                No languages added yet — the panel will fall back to the default single button until you add at least one.
              </p>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-dashed w-full sm:w-auto"
              onClick={() =>
                update("language_variants", [
                  ...languageVariants,
                  { id: `lv_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, button_label: "New language", questions: [] },
                ])
              }
            >
              <Plus className="h-4 w-4" />
              Add language {languageVariants.length > 0 ? `(${languageVariants.length})` : ""}
            </Button>
          </div>
        )}
      </div>

      <Field
        label="Accepted role"
        hint="Role added when an admin accepts an application"
      >
        <Select
          value={cfg.accepted_role_id || ""}
          onValueChange={(v) =>
            update("accepted_role_id", v === "__none__" ? "" : v)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="__none__">(none)</SelectItem>
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                @{r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </Section>
  );
}
